import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { BigNumber } from "shared/cubiq-model-multi-asset";
import { TranslateService } from "@ngx-translate/core";
import { SystemConfigPromiseService } from "shared/auth-services";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { ICcyPairLookupService } from "../../lookup/model/ICcyPairLookupService";
import { CcyLookupService } from "../../lookup/model/impl/CcyLookupService";
import { SysFeatures } from "../../model/core/SysFeatures";

export const BMARK_DEFAULT_NAME = "BM";

export class OrderTransactionHelper {
  private typeBMName: string = BMARK_DEFAULT_NAME;

  constructor(
    private translate: TranslateService,
    private _ccyLookup: CcyLookupService,
    private _ccyPairLookup: ICcyPairLookupService,
    private _sysConfigService: SystemConfigPromiseService,
  ) {
    this.getBMShortNameFromSysConfig().then((typeBMName: string) => {
      this.typeBMName = typeBMName;
    });
  }

  retrieveData(item: any, columnProperty: string): string {
    return this.perform(item, columnProperty);
  }

  retrieveSort(item: any, columnProperty: string): string {
    return this.perform(item, columnProperty);
  }

  retrieveSearch(order: any, columnProperty: string): any {
    return this.retrieveSort(order, columnProperty);
  }

  /* 
		Limit Order Entry/Reval: <Dealt Dir> < Remaining Amt> <Dealt Ccy> @ <Request Rate> <RequestOrderType> <Value Date>. 
		Limit Order Fill: <Dealt Dir> <Fill Amt> <Dealt Ccy> Filled @ <Client All-In> <RequestOrderType> <Value Date>. 
	*/

  perform(item: any, columnProperty: string): string {
    if (!item) {
      //||!(item instanceof CubiqTS.InterventionEventImpl)) {
      logger.out.warn("Unknown type for item");
      return "-";
    }
    const deal = item.deal;
    const reason = item.reason;
    let transactionDesc: string;

    if (item.eventType === CubiqTS.InterventionEventType.PORTFOLIO_MANUAL_PRICING) {
      if (item.fwdRollOrderData.fixingCd != null) {
        transactionDesc = this.getBMFwdRollTransaction(item);
      } else if (item.fwdRollOrderData.orders?.length == 1) {
        // Algo Fwd Roll trnsactions will be processed as normal order desc
        transactionDesc = this.getOrderTransactionDescription(
          item.fwdRollOrderData.orders[0],
          reason,
          deal,
        );
      }
      if (transactionDesc) {
        return transactionDesc;
      }
    }

    let error: boolean = false;
    let order = item.orderData;
    if (item.eventType === CubiqTS.InterventionEventType.FIXING_RATE) {
      try {
        if (item.fixingRateRequirement != null) {
          let ffr = item.fixingRateRequirement;
          if (
            ffr.commentsTxt instanceof CubiqTS.ErrorsImpl &&
            Array.isArray(ffr.commentsTxt.errorMessages) &&
            ffr.commentsTxt.errorMessages.length > 0
          ) {
            for (let errorMsg of ffr.commentsTxt.errorMessages) {
              let errorMessage: any = JSON.parse(
                errorMsg.jsonMessage != null ? errorMsg.jsonMessage : errorMsg,
              );
              errorMessage = !Array.isArray(errorMessage) ? [errorMessage] : errorMessage;
              for (let jsonProperty of errorMessage) {
                if (typeof jsonProperty.args[1] === "string" && jsonProperty.args[1].length <= 5) {
                  // server time sent in utc as string
                  jsonProperty.args[1] = item.fixingRateRequirement.fixingTime.format("HH:mm");
                }
                transactionDesc = this.translate.instant(jsonProperty.id, jsonProperty["args"]);
              }
            }
          } else {
            transactionDesc =
              item.fixingRateRequirement.rateSourceCd +
              " " +
              item.fixingRateRequirement.fixingTime.format("HH:mm");
          }
        } else {
          error = true;
        }
      } catch (ex) {
        error = true;
      }
    } else if (order instanceof CubiqTS.OrderDataImpl) {
      transactionDesc = this.getOrderTransactionDescription(order, reason, deal);
      if (transactionDesc == null) {
        logger.out.warn("Cannot derive transaction description for " + item.eventId);
        error = true;
      }
    }

    if (error) {
      return "-";
    } else {
      return transactionDesc;
    }
  }

  private getOrderTransactionDescription(
    order: CubiqTS.OrderData,
    reason: CubiqTS.DIReason,
    deal: CubiqTS.Deal,
  ): string {
    let retDesc = "";

    let isBenchmark: boolean =
      order.orderType === CubiqTS.OrderType.BENCHMARK ||
      order.orderType === CubiqTS.OrderType.BMALGO;

    /* ***************************** DEFINE PROPERTIES */
    // CLIENT INTENT
    let clientIntent: string = this.getOrderIntent(order);

    // REMAINING AMT to be Filled
    let leavesQty: string = this.getLeavesQtyDescription(order);

    // DEALT CURRENCY
    let dealtCcy = "-";
    if (typeof order.fixedCode === "string") {
      dealtCcy = order.fixedCode;
    }

    // REQUEST ORDER TYPE
    let reqOrderType = this.resolveRequestOrderType(order.requestedOrderType);

    // TENOR & VALUE DATE
    let settleDateTenor = this.getOrderValueDateDescription(isBenchmark, order);

    /* ***************************** COMPOSE TRANSACTION COLUMN */
    if (
      reason === CubiqTS.DIReason.SOFT_CUT_OFF ||
      reason === CubiqTS.DIReason.CANCEL_DURING_SOFT_CUT_OFF ||
      (reason === CubiqTS.DIReason.ENTRY_CREDIT_FAIL && isBenchmark) ||
      (reason === CubiqTS.DIReason.ENTRY_CREDIT_PROXIMITY_FAIL && isBenchmark) ||
      (reason === CubiqTS.DIReason.MODIFY_CREDIT_FAIL && isBenchmark) ||
      (reason === CubiqTS.DIReason.MODIFY_CREDIT_PROXIMITY_FAIL && isBenchmark) ||
      (reason === CubiqTS.DIReason.FORCE_TO_DI && isBenchmark)
    ) {
      retDesc += `${clientIntent} ${leavesQty} ${dealtCcy} ${this.typeBMName} ${settleDateTenor}`;
    } else if (
      reason === CubiqTS.DIReason.ENTRY_CREDIT_FAIL ||
      reason === CubiqTS.DIReason.ENTRY_CREDIT_PROXIMITY_FAIL ||
      reason === CubiqTS.DIReason.CREDIT_CANCEL_FAIL ||
      reason === CubiqTS.DIReason.CREDIT_REJECT_FAIL ||
      reason === CubiqTS.DIReason.CREDIT_EXPIRY_FAIL ||
      reason === CubiqTS.DIReason.CREDIT_CANCEL_TIMEOUT ||
      reason === CubiqTS.DIReason.CREDIT_REJECT_TIMEOUT ||
      reason === CubiqTS.DIReason.CREDIT_EXPIRY_TIMEOUT ||
      reason === CubiqTS.DIReason.MODIFY_CREDIT_FAIL ||
      reason === CubiqTS.DIReason.MODIFY_CREDIT_PROXIMITY_FAIL ||
      reason === CubiqTS.DIReason.DAILY_REVAL_CREDIT_FAIL ||
      reason === CubiqTS.DIReason.PROXIMITY_REVAL_CREDIT_FAIL ||
      reason === CubiqTS.DIReason.FORCE_TO_DI ||
      reason === CubiqTS.DIReason.FILL_FWD_ROLL_NOT_RECEIVED
    ) {
      if (order.requestedOrderType === CubiqTS.RequestOrderType.MARKET) {
        retDesc += `${clientIntent} ${leavesQty} ${dealtCcy} ${reqOrderType} ${settleDateTenor}`;
      } else {
        // REQUEST RATE
        let requestRate: string = "-";
        let requestRateValue = this.getValue(order, "requestRt");
        if (requestRateValue != null) {
          requestRate = this.getFormattedValue(order, requestRateValue);
        }
        retDesc += `${clientIntent} ${leavesQty} ${dealtCcy} @${requestRate} ${reqOrderType} ${settleDateTenor}`;
      }
    } else if (
      reason === CubiqTS.DIReason.PORTFOLIO_FWD_ROLL ||
      reason === CubiqTS.DIReason.PORTFOLIO_FWD_ROLL_SAVED ||
      reason === CubiqTS.DIReason.PORTFOLIO_FWD_ROLL_AUTO_SAVED ||
      reason === CubiqTS.DIReason.PORTFOLIO_FWD_ROLL_ERROR
    ) {
      let dealtAmt = this.getDealtAmt(order, deal);

      if (order.orderType === CubiqTS.OrderType.ALGO) {
        dealtAmt = order.executedQty.toFormat(0);
      }

      retDesc = `${clientIntent} ${dealtAmt} ${dealtCcy} ${reqOrderType} ${settleDateTenor}`;
    } else if (
      reason === CubiqTS.DIReason.FILL_CREDIT_FAIL ||
      reason === CubiqTS.DIReason.FILL_WITHELD ||
      reason === CubiqTS.DIReason.FILL_NOT_RECEIVED
    ) {
      let dealtAmt = this.getDealtAmt(order, deal);

      if (order.requestedOrderType === CubiqTS.RequestOrderType.MARKET) {
        retDesc = `${clientIntent} ${dealtAmt} ${dealtCcy} Filled ${reqOrderType} ${settleDateTenor}`;
      } else {
        let allInClientRate = "-";
        let allInClientRateValue = deal != null ? this.getValue(deal, "allInClientRate") : null;
        if (allInClientRateValue != null) {
          allInClientRate = this.getFormattedValue(order, allInClientRateValue);
        }
        retDesc = `${clientIntent} ${dealtAmt} ${dealtCcy} Filled @ ${allInClientRate} ${reqOrderType} ${settleDateTenor}`;
      }
    } else {
      retDesc = null;
    }
    return retDesc;
  }

  private getDealtAmt(order: CubiqTS.OrderData, deal: CubiqTS.Deal): string {
    let retAmt = "-";
    let orderQtyValue = deal != null ? deal.amt1 : order.orderQty;

    if (orderQtyValue instanceof CubiqTS.BigNumber) {
      retAmt = orderQtyValue.toFormat(0);
    }
    return retAmt;
  }

  /**
   * If there's just a single Fwd allocation in the orders then we return it's description otherwise we
   * return the count
   * @param item
   * @returns
   */
  private getBMFwdRollTransaction(item: CubiqTS.InterventionEvent): string {
    const fwdRollData = item.fwdRollOrderData;

    if (fwdRollData?.orders?.length > 0) {
      const orders = fwdRollData.orders;

      let nonSpotOrder = null;
      let nonSpotAllocation = null;
      let nonSpotCount = 0;
      orders.forEach((orderData) => {
        orderData.allocationData.forEach((allocation) => {
          if (allocation.settleTenor !== CubiqTS.Tenor.SPOT) {
            nonSpotOrder = orderData;
            nonSpotAllocation = allocation;
            nonSpotCount++;
          }
        });
      });
      if (nonSpotCount == 1) {
        return this.getAllocationDescription(nonSpotOrder, nonSpotAllocation);
      }
      return `${nonSpotCount} allocations`;
    }
    return "-";
  }

  /**
   * The text description of the allocation for use in a transaction column
   * @param orderRef
   * @param allocation
   * @returns
   */
  private getAllocationDescription(
    order: CubiqTS.OrderData,
    allocation: CubiqTS.AllocationDataImpl,
  ): string {
    const clientIntent: string = this.getAllocationIntent(allocation);
    const leavesQty: string = this.getLeavesQtyDescription(allocation);
    let dealtCcy = "-";
    if (typeof allocation.fixedCcy === "string") {
      dealtCcy = allocation.fixedCcy;
    }
    const settleDateTenor = this.getAllocationValueDateDescription(allocation);
    const orderProduct = this.getBenchmarkProductLabel(order.product);

    // Order ID 123 EURUSD Buy 2M EUR Spot 18-Jul-2022 - Swap
    return `Order ID ${order.orderRef}: ${clientIntent} ${leavesQty} ${dealtCcy} ${this.typeBMName} ${settleDateTenor} - ${orderProduct}`;
  }

  private getOrderValueDateDescription(isBenchmark: boolean, order: CubiqTS.OrderDataImpl) {
    let settleDateTenor = order.settleTenor != null ? this.resolveTenor(order.settleTenor) : "BRKN";

    if (order.settleDate && CubiqTS.moment.isMoment(order.settleDate)) {
      let settleDate = CubiqTS.moment(order.settleDate).utc().format("DD-MMM-YYYY");
      return `${settleDateTenor} (${settleDate})`;
    }

    return settleDateTenor;
  }

  private getAllocationValueDateDescription(allocation: CubiqTS.AllocationDataImpl) {
    let settleDateTenor =
      allocation.settleTenor != null ? this.resolveTenor(allocation.settleTenor) : "BRKN";

    let settleDate =
      allocation.settleDate && CubiqTS.moment.isMoment(allocation.settleDate)
        ? CubiqTS.moment(allocation.settleDate).utc().format("DD-MMM-YYYY")
        : "";

    return `${settleDateTenor} (${settleDate})`;
  }

  private getLeavesQtyDescription(order: { leavesQty: BigNumber }) {
    let leavesQtyDesc: string = order?.leavesQty ? order.leavesQty.toFormat(0) : "-";
    return leavesQtyDesc;
  }

  /**
   * Get fixing cell text for Intervention Blotter
   *
   * @param fixingDate
   * @param fixingSourceCd
   * @param displayTZ
   * @returns
   */
  public static getBMFixingDescription(
    fixingDate: moment.Moment,
    fixingSourceCd: string,
    displayTZ: CubiqTS.TimeZone,
  ): string {
    const fixingCode = fixingSourceCd ? `${fixingSourceCd} ` : "";
    const fixingTz: string = displayTZ ? ` ${displayTZ.id}` : " ";
    let fixingDateStr: string;
    let fixingTimeStr: string;

    if (displayTZ) {
      const tzMoment = CubiqTS.moment.tz(fixingDate.valueOf(), displayTZ.javaTimeZoneCd);
      fixingDateStr = tzMoment.format("DD-MMM-YY");
      fixingTimeStr = tzMoment.format("HH:mm");
    } else {
      fixingDateStr = fixingDate.format("DD-MMM-YY");
      fixingTimeStr = fixingDate.format("HH:mm");
    }
    return `${fixingCode}${fixingTimeStr}${fixingTz}(${fixingDateStr})`;
  }

  private getOrderIntent(order: CubiqTS.OrderDataImpl) {
    return this.getIntent(order?.clientIntent);
  }

  private getAllocationIntent(order: CubiqTS.AllocationDataImpl) {
    return this.getIntent(order?.allocationIntent);
  }

  private getIntent(intent: CubiqTS.OrderIntent) {
    if (intent === CubiqTS.OrderIntent.BUY) {
      return "Buy";
    } else if (intent === CubiqTS.OrderIntent.SELL) {
      return "Sell";
    }
    return "";
  }

  /* Rate functions START */

  getValue(order: any, columnProperty: string): BigNumber | CubiqTS.BigNumber {
    if (order[columnProperty] != undefined && order[columnProperty] != null)
      return new CubiqTS.BigNumber(order[columnProperty]);
    return null;
  }

  getFormattedValue(order: any, rate: BigNumber | CubiqTS.BigNumber): string {
    let minPrecision: number = this.getMinPrecision(order);
    let currentDecs: number = rate.decimalPlaces();
    if (currentDecs < minPrecision) {
      return rate.toFixed(minPrecision);
    }
    return rate.toFixed(currentDecs);
  }

  getMinPrecision(order: any): number {
    let ccyPair = this.getCcyPair(order);
    if (ccyPair) {
      if (order["isInvert"]) {
        return ccyPair.spotInvertDecimalsQty;
      } else {
        return ccyPair.pipsQty;
      }
    }
    return 0;
  }
  private getCcyPair(order: any): any {
    if (this._ccyPairLookup) {
      return this._ccyPairLookup.getCcyPair(order["baseCcyCd"], order["nonBaseCcyCd"]);
    }
    return null;
  }

  public getBenchmarkProductLabel(product: CubiqTS.Product): string {
    switch (product) {
      case CubiqTS.Product.FWD:
        return "FWD";
      case CubiqTS.Product.SSP:
        return "SSP";
      case CubiqTS.Product.SWP:
        return "Swap";
      case CubiqTS.Product.SPT:
        return "SP";
      default:
        // Hmm, should not have gotten here
        return "(Code " + product + ")";
    }
  }

  /* Rate functions END */
  private numberToMills(value: number): string {
    let result = "";
    result = Math.round((value / 1000000) * 100 + Number.EPSILON) / 100 + " M";
    return result;
  }

  private resolveProduct(productType: number): string {
    if (productType !== undefined && productType !== null) {
      switch (productType) {
        case CubiqTS.Product.SPT:
          return "SPT";
        case CubiqTS.Product.FWD:
          return "FWD";
        case CubiqTS.Product.NDF:
          return "NDF";
      }
    }
  }

  private getBMShortNameFromSysConfig(): Promise<string> {
    if (this._sysConfigService) {
      return new Promise<string>((res, rej) => {
        this._sysConfigService
          .getSystemConfig(SysFeatures.fixingOrderShortName)
          .then((value) => {
            let returnValue = value != null && value.length > 0 ? value : BMARK_DEFAULT_NAME;
            res(returnValue);
          })
          .catch((error) => {
            logger.out.error(
              `OrderTypeAdapter : getSystemConfig :  failed to get system config for: ${SysFeatures.fixingOrderShortName}`,
            );
            res("");
          });
      });
    }
  }

  private resolveRequestOrderType(orderType: number): string {
    if (orderType !== undefined && orderType != null) {
      switch (orderType) {
        case CubiqTS.RequestOrderType.TP:
          return "TP ";
        case CubiqTS.RequestOrderType.CALL:
          return "CALL ";
        case CubiqTS.RequestOrderType.BMARK:
          return "BMARK ";
        case CubiqTS.RequestOrderType.SL_ASK:
          return "SL ASK ";
        case CubiqTS.RequestOrderType.SL_BID:
          return "SL BID ";
        case CubiqTS.RequestOrderType.SL_ATRATE:
          return "SL ";
        case CubiqTS.RequestOrderType.ALGO:
          return "ALGO ";
        case CubiqTS.RequestOrderType.TSL:
          return "TSL ";
        case CubiqTS.RequestOrderType.TSL_BID:
          return "TSL BID ";
        case CubiqTS.RequestOrderType.TSL_ASK:
          return "TSL ASK ";
        case CubiqTS.RequestOrderType.MARKET:
          return "MKT";
      }
    }
  }
  private resolveTenor(settleTenor: number): string {
    if (settleTenor !== undefined && settleTenor != null) {
      switch (settleTenor) {
        case CubiqTS.Tenor.EMPTY:
          return "EMPTY";
        case CubiqTS.Tenor.TDY:
          return "TDY";
        case CubiqTS.Tenor.TOM:
          return "TOM";
        case CubiqTS.Tenor.SPOT:
          return "SP";
        case CubiqTS.Tenor.SN:
          return "SN";
        case CubiqTS.Tenor.W1:
          return "1W";
        case CubiqTS.Tenor.W2:
          return "2W";
        case CubiqTS.Tenor.W3:
          return "3W";
        case CubiqTS.Tenor.M1:
          return "1M";
        case CubiqTS.Tenor.M2:
          return "2M";
        case CubiqTS.Tenor.M3:
          return "3M";
        case CubiqTS.Tenor.M4:
          return "4M";
        case CubiqTS.Tenor.M5:
          return "5M";
        case CubiqTS.Tenor.M6:
          return "6M";
        case CubiqTS.Tenor.M7:
          return "7M";
        case CubiqTS.Tenor.M8:
          return "8M";
        case CubiqTS.Tenor.M9:
          return "9M";
        case CubiqTS.Tenor.M10:
          return "10M";
        case CubiqTS.Tenor.M11:
          return "11M";
        case CubiqTS.Tenor.Y1:
          return "1Y";
        case CubiqTS.Tenor.M13:
          return "13M";
        case CubiqTS.Tenor.M14:
          return "14M";
        case CubiqTS.Tenor.M15:
          return "15M";
        case CubiqTS.Tenor.M16:
          return "16M";
        case CubiqTS.Tenor.M17:
          return "17M";
        case CubiqTS.Tenor.M18:
          return "18M";
        case CubiqTS.Tenor.M19:
          return "19M";
        case CubiqTS.Tenor.M20:
          return "20M";
        case CubiqTS.Tenor.M21:
          return "21M";
        case CubiqTS.Tenor.M22:
          return "22M";
        case CubiqTS.Tenor.M23:
          return "23M";
        case CubiqTS.Tenor.Y2:
          return "2Y";
        case CubiqTS.Tenor.Y3:
          return "3Y";
        case CubiqTS.Tenor.Y4:
          return "4Y";
        case CubiqTS.Tenor.Y5:
          return "5Y";
        case CubiqTS.Tenor.Y6:
          return "6Y";
        case CubiqTS.Tenor.Y7:
          return "7Y";
        case CubiqTS.Tenor.Y8:
          return "8Y";
        case CubiqTS.Tenor.Y9:
          return "9Y";
        case CubiqTS.Tenor.Y10:
          return "10Y";
        case CubiqTS.Tenor.ALL:
          return "ALL";
      }
    }
  }
}
